/**
 * ServerFormModal
 * 서버 추가/수정 모달
 */

import React, { useState, useEffect } from 'react';
import type { DeploymentServer } from './ServerManagementPage';

interface Organization {
  id: string;
  name: string;
  service_type?: string;
  is_approved?: boolean;
  account_status?: string;
}

interface Props {
  server?: DeploymentServer | null;
  onClose: () => void;
  onSuccess: () => void;
}

export const ServerFormModal: React.FC<Props> = ({ server, onClose, onSuccess }) => {
  const isEdit = !!server;

  const [form, setForm] = useState({
    server_code: server?.server_code || '',
    customer_name: server?.customer_name || '',
    organization_id: server?.organization_id || '',
    hostname: server?.hostname || '',
    local_ip: server?.local_ip || '',
    tailscale_ip: server?.tailscale_ip || '',
    ssh_user: server?.ssh_user || 'ntd',
    ssh_port: server?.ssh_port || 22,
    deploy_mode: server?.deploy_mode || 'local_ubuntu',
    os_info: server?.os_info || '',
    cpu: server?.hardware_info?.cpu || '',
    ram: server?.hardware_info?.ram || '',
    disk: server?.hardware_info?.disk || '',
    notes: server?.notes || '',
    installed_at: server?.installed_at ? server.installed_at.split('T')[0] : '',
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [orgLoading, setOrgLoading] = useState(true);

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        const response = await fetch('/api/v1/admin/owner-approvals/all', {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
          },
        });
        if (response.ok) {
          const data = await response.json();
          setOrganizations(data.organizations || data || []);
        }
      } catch {
        // 조직 목록 로드 실패해도 폼은 사용 가능
      } finally {
        setOrgLoading(false);
      }
    };
    fetchOrganizations();
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.server_code.trim() || !form.customer_name.trim()) {
      setError('서버 코드와 고객사명은 필수입니다');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const payload: any = {
        server_code: form.server_code.trim(),
        customer_name: form.customer_name.trim(),
        hostname: form.hostname.trim() || form.server_code.trim(),
        local_ip: form.local_ip.trim() || null,
        tailscale_ip: form.tailscale_ip.trim() || null,
        ssh_user: form.ssh_user.trim() || 'ntd',
        ssh_port: Number(form.ssh_port) || 22,
        deploy_mode: form.deploy_mode,
        os_info: form.os_info.trim() || null,
        notes: form.notes.trim() || null,
        installed_at: form.installed_at || null,
      };

      if (form.organization_id.trim()) {
        payload.organization_id = form.organization_id.trim();
      }

      // hardware_info
      const hw: Record<string, string> = {};
      if (form.cpu.trim()) hw.cpu = form.cpu.trim();
      if (form.ram.trim()) hw.ram = form.ram.trim();
      if (form.disk.trim()) hw.disk = form.disk.trim();
      if (Object.keys(hw).length > 0) {
        payload.hardware_info = hw;
      }

      const url = isEdit
        ? `/api/admin/servers/${server!.id}`
        : '/api/admin/servers';

      const response = await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.detail || '저장 실패');
      }

      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : '오류 발생');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* 헤더 */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-bold text-gray-900">
            {isEdit ? '서버 수정' : '서버 추가'}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
        </div>

        {/* 폼 */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              {error}
            </div>
          )}

          {/* 기본 정보 */}
          <div className="border-b pb-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">기본 정보</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  서버 코드 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="server_code"
                  value={form.server_code}
                  onChange={handleChange}
                  placeholder="ntd-study-001"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  고객사명 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="customer_name"
                  value={form.customer_name}
                  onChange={handleChange}
                  placeholder="이루는 자리"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  required
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">호스트명</label>
                <input
                  type="text"
                  name="hostname"
                  value={form.hostname}
                  onChange={handleChange}
                  placeholder="ntd-study-001"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">연결 사업자</label>
                <select
                  name="organization_id"
                  value={form.organization_id}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg"
                  disabled={orgLoading}
                >
                  <option value="">{orgLoading ? '로딩 중...' : '선택 안함'}</option>
                  {organizations.map((org) => (
                    <option key={org.id} value={org.id}>
                      {org.name}{org.service_type ? ` (${org.service_type})` : ''}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* 네트워크 */}
          <div className="border-b pb-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">네트워크</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tailscale IP</label>
                <input
                  type="text"
                  name="tailscale_ip"
                  value={form.tailscale_ip}
                  onChange={handleChange}
                  placeholder="100.x.x.x"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">로컬 IP</label>
                <input
                  type="text"
                  name="local_ip"
                  value={form.local_ip}
                  onChange={handleChange}
                  placeholder="192.168.x.x"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">SSH 사용자</label>
                <input
                  type="text"
                  name="ssh_user"
                  value={form.ssh_user}
                  onChange={handleChange}
                  placeholder="ntd"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">SSH 포트</label>
                <input
                  type="number"
                  name="ssh_port"
                  value={form.ssh_port}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
            </div>
          </div>

          {/* 서버 환경 */}
          <div className="border-b pb-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">서버 환경</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">배포 모드</label>
                <select
                  name="deploy_mode"
                  value={form.deploy_mode}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg"
                >
                  <option value="local_ubuntu">로컬 우분투</option>
                  <option value="local_windows">로컬 윈도우</option>
                  <option value="cloud">클라우드</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">OS 정보</label>
                <input
                  type="text"
                  name="os_info"
                  value={form.os_info}
                  onChange={handleChange}
                  placeholder="Ubuntu 24.04 LTS"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4 mt-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">CPU</label>
                <input
                  type="text"
                  name="cpu"
                  value={form.cpu}
                  onChange={handleChange}
                  placeholder="Intel N100"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">RAM</label>
                <input
                  type="text"
                  name="ram"
                  value={form.ram}
                  onChange={handleChange}
                  placeholder="16GB"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">디스크</label>
                <input
                  type="text"
                  name="disk"
                  value={form.disk}
                  onChange={handleChange}
                  placeholder="256GB SSD"
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
            </div>
          </div>

          {/* 기타 */}
          <div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">설치일</label>
                <input
                  type="date"
                  name="installed_at"
                  value={form.installed_at}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
            </div>
            <div className="mt-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">메모</label>
              <textarea
                name="notes"
                value={form.notes}
                onChange={handleChange}
                rows={3}
                placeholder="서버 관련 메모"
                className="w-full px-3 py-2 border rounded-lg"
              />
            </div>
          </div>

          {/* 버튼 */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border rounded-lg text-gray-700 hover:bg-gray-50"
            >
              취소
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? '저장 중...' : isEdit ? '수정' : '추가'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
